import { useEffect } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { ErrorView } from "@ps-design-system/components/ErrorView";
import { connect } from "react-redux";

import { Spinner } from "shared-components/decorators/spinner";

import { trackHostHubViewed } from "../../app-host-hub/analytics/host-hub-analytics";
import { HOST_HUB_TAB_IDS } from "../../app-host-hub/constants/host-hub.consts";
import HostHubLayout from "../../app-host-hub/components/HostHubLayout";
import HostHubWelcomeDialog from "../../app-host-hub/components/HostHubWelcomeDialog";
import type { HostHubNavTabId } from "../../app-host-hub/types/host-hub.types";

import type {
  HostHubContainerProps,
  HostHubProfile,
} from "./HostHubContainer.types";
import { navigateToHostHubTab } from "./HostHubContainer.utils";

const profileLoadingShellClassName =
  "tw-flex tw-min-h-[calc(100dvh-72px)] tw-items-center tw-justify-center tw-bg-gray-100";

const HostHubContainerComponent = ({
  params,
  profile,
  SSOid,
}: HostHubContainerProps) => {
  const activeTabId =
    params?.tab === HOST_HUB_TAB_IDS.metrics
      ? HOST_HUB_TAB_IDS.metrics
      : HOST_HUB_TAB_IDS.overview;
  const handleActiveTabIdChange = (nextTabId: HostHubNavTabId) =>
    navigateToHostHubTab(nextTabId, activeTabId);
  const isProfileReady = profile.hasFetched && !profile.isFetching;
  const isHost = Boolean(profile.data?.is_host);
  const profileLoadFailed = isProfileReady && !SSOid;

  useEffect(() => {
    if (!isProfileReady) {
      return;
    }
    if (!SSOid) {
      datadogRum.addError(new Error("Host Hub profile failed to load"));
      return;
    }
    if (!isHost) {
      window.location.href = "/";
      return;
    }
    trackHostHubViewed({ ssoid: SSOid });
  }, [isHost, isProfileReady, SSOid]);

  if (profileLoadFailed) {
    return (
      <div className={profileLoadingShellClassName}>
        <ErrorView />
      </div>
    );
  }

  if (!isProfileReady) {
    return (
      <div className={profileLoadingShellClassName}>
        <Spinner additionalStyle="gray" />
      </div>
    );
  }

  return (
    <>
      <HostHubLayout
        activeTabId={activeTabId}
        email={profile.data?.email}
        firstName={profile.data?.first_name}
        lastName={profile.data?.last_name}
        onActiveTabIdChange={handleActiveTabIdChange}
        SSOid={SSOid}
      />
      <HostHubWelcomeDialog
        isHost={isHost}
        ssoId={SSOid}
      />
    </>
  );
};

const mapStateToProps = (state: {
  profile: HostHubProfile & { SSOid?: string };
}) => ({
  profile: state.profile,
  SSOid: state.profile.SSOid,
});

const HostHubContainer = connect(mapStateToProps)(HostHubContainerComponent);

export default HostHubContainer;
